import type { SVGProps } from 'react';

import { vectorRegistry, vectorUrlRegistry, type VectorImageName } from '~assets/vectors/generated';

export type { VectorImageName };

export interface Props extends Omit<SVGProps<SVGSVGElement>, 'color'> {
	/** Namespaced token, e.g. "icon/plus" or "other/webhooks". */
	name: VectorImageName;
	/** Shorthand that sets both width and height. `width`/`height` still override. */
	size?: number | string;
	/** Tint colour. Defaults to the inherited `currentColor`. */
	color?: string;
	/**
	 * Render the SVG with its authored colours instead of `currentColor`.
	 * Use for multi-colour assets (file-type badges, brand logos). In this mode
	 * the asset is drawn from its URL via `<img>`, so `color` does not apply and
	 * only layout props (`size`/`width`/`height`/`className`/`style`) are forwarded.
	 */
	original?: boolean;
}

/**
 * Renders a vector (SVG) asset by name: `<VectorImage name="icon/plus" size={20} />`.
 *
 * Mono icons inherit `currentColor`, so `color` tints them. For multi-colour
 * assets pass `original` to keep the authored colours. Decorative by default
 * (`aria-hidden`); pass `aria-label` + `role="img"` when meaningful.
 */
const VectorImage = ({
	name,
	size,
	color,
	original = false,
	width,
	height,
	className,
	style,
	...props
}: Props) => {
	const resolvedWidth = width ?? size;
	const resolvedHeight = height ?? size;

	if (original) {
		return (
			<img
				src={vectorUrlRegistry[name]}
				width={resolvedWidth}
				height={resolvedHeight}
				className={className}
				style={style}
				alt=''
				aria-hidden
			/>
		);
	}

	const Component = vectorRegistry[name];
	return (
		<Component
			// Only override dimensions when given. Passing `width={undefined}` would
			// strip the SVG's own width/height (svgr keeps them) and make it stretch
			// to fill its parent — so when unsized we leave the intrinsic size intact.
			{...(resolvedWidth != null ? { width: resolvedWidth } : {})}
			{...(resolvedHeight != null ? { height: resolvedHeight } : {})}
			color={color}
			className={className}
			style={style}
			aria-hidden
			{...props}
		/>
	);
};

export default VectorImage;
